import React, { useEffect, useState } from "react";
import api, { fmtLargeNumber } from "@/lib/api";
import { useLang, t } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

const AnalysisList = () => {
  const { lang } = useLang();
  const [stocks, setStocks] = useState([]);
  const [search, setSearch] = useState("");
  const [market, setMarket] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [params] = useSearchParams();

  useEffect(() => {
    const m = params.get("market");
    if (m) setMarket(m);
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    setCurrentPage(1);
    const fetchStocks = async (attempt = 0) => {
      try {
        setLoading(true);
        setError(null);
        const q = { params: {} };
        if (market !== "all") q.params.market = market;
        if (search) q.params.search = search;
        const r = await api.get("/stocks", q);
        if (!cancelled) {
          setStocks(r.data.stocks || []);
          setLoading(false);
        }
      } catch (e) {
        if (cancelled) return;
        if (attempt < 1) {
          setTimeout(() => fetchStocks(attempt + 1), 1500);
          return;
        }
        setError(e.message || "Network error");
        setLoading(false);
      }
    };
    fetchStocks();
    return () => { cancelled = true; };
  }, [search, market]);

  const totalPages = Math.ceil(stocks.length / itemsPerPage);
  const currentStocks = stocks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="analysis-page">
      <div className="overline text-white/40 mb-3">{t(lang, "Database Saham", "Stock Database")}</div>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-8">
        {t(lang, "Analisa Saham", "Stock Analysis")}
      </h1>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="stock-search-input"
            placeholder={t(lang, "Cari BBCA, AAPL, NVDA...", "Search BBCA, AAPL, NVDA...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
          />
        </div>
        <div className="flex border border-white/10 rounded-md overflow-hidden" data-testid="market-filter">
          {[
            { id: "all", label_id: "Semua", label_en: "All" },
            { id: "id", label_id: "Indonesia", label_en: "Indonesia" },
            { id: "us", label_id: "USA", label_en: "USA" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setMarket(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-5 text-sm font-medium transition-colors ${market === f.id ? "bg-white text-black" : "text-white/60 hover:text-white hover:bg-white/5"}`}
            >
              {t(lang, f.label_id, f.label_en)}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="stocks-table">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 bg-white/5 overline text-white/50">
          <div className="col-span-3">{t(lang, "Simbol", "Symbol")}</div>
          <div className="col-span-3 hidden md:block">{t(lang, "Sektor", "Sector")}</div>
          <div className="col-span-3 md:col-span-2 text-right">{t(lang, "Harga", "Price")}</div>
          <div className="col-span-2 text-right">Δ%</div>
          <div className="col-span-2 text-right hidden md:block">P/E</div>
          <div className="col-span-2 text-right">Market Cap</div>
        </div>
        {loading && stocks.length === 0 && (
          <div className="p-10 text-center text-white/50" data-testid="stocks-loading">
            {t(lang, "Memuat data saham...", "Loading stocks...")}
          </div>
        )}
        {error && !loading && (
          <div className="p-10 text-center" data-testid="stocks-error">
            <div className="text-rose-400 mb-2 font-mono text-sm">⚠️ {error}</div>
            <div className="text-white/50 text-sm max-w-md mx-auto">
              {t(lang,
                "Tidak bisa terhubung ke server. Cek koneksi internet, disable browser extension (ad blocker/privacy), atau coba refresh halaman.",
                "Cannot reach server. Check internet, disable browser extensions (ad blocker/privacy), or refresh page.")}
            </div>
            <button
              onClick={() => window.location.reload()}
              data-testid="retry-btn"
              className="mt-4 px-5 py-2 border border-white/20 rounded-full text-sm hover:bg-white/5"
            >
              {t(lang, "Coba Lagi", "Retry")}
            </button>
          </div>
        )}
        {currentStocks.map((s) => (
          <Link
            key={s.symbol}
            to={`/analysis/${encodeURIComponent(s.symbol)}`}
            data-testid={`stock-row-${s.symbol}`}
            className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 hover:bg-white/5 transition-colors items-center"
          >
            <div className="col-span-3">
              <div className="font-display font-bold text-sm">{s.symbol}</div>
              <div className="text-xs text-white/50 truncate">{s.name}</div>
            </div>
            <div className="col-span-3 hidden md:block text-sm text-white/60">{s.sector}</div>
            <div className="col-span-3 md:col-span-2 text-right font-mono text-sm">
              {s.currency === "IDR" ? s.price.toLocaleString("id-ID") : s.price.toFixed(2)}
            </div>
            <div className={`col-span-2 text-right font-mono text-sm ${s.change_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {s.change_pct >= 0 ? "+" : ""}{s.change_pct.toFixed(2)}%
            </div>
            <div className="col-span-2 text-right font-mono text-sm hidden md:block">{s.pe > 0 ? s.pe.toFixed(1) : "—"}</div>
            <div className="col-span-2 text-right font-mono text-xs text-white/60">{fmtLargeNumber(s.market_cap)}</div>
          </Link>
        ))}
        {stocks.length === 0 && !loading && !error && <div className="p-10 text-center text-white/50">{t(lang, "Tidak ada hasil", "No results")}</div>}
        
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 p-6 border-t border-white/10">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-white/20 rounded-md text-sm hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t(lang, "Sebelumnya", "Previous")}
            </button>
            <div className="text-sm text-white/60">
              {currentPage} / {totalPages}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-white/20 rounded-md text-sm hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t(lang, "Selanjutnya", "Next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisList;
